import {createRule, type Maybe} from '@regle/core';
import {email, isFilled, required, withMessage} from '@regle/rules';
import {computed, type Ref, useAuth, useTranslation} from '#imports';
import {useFeatureFlag} from '@malt/feature-flags';
import {inferRules} from '@malt/forms';
import {EInvoiceIdentifierType, InvoicingModeResource, type ServiceLevelFeaturesResource} from '~/openapi';
import type {LegalEntityForm} from '~/types/legalEntity';

export function useLegalEntityFormValidation(formData: Ref<LegalEntityForm>, serviceLevelCompanyFeatures?: Ref<ServiceLevelFeaturesResource | null>) {
    const {t} = useTranslation();
    const {hasEnoughPrivilege} = useAuth();
    const enablePrepaymentForUP = useFeatureFlag('enable-prepayment-for-up-clients');

    const baseKey = 'client.organization.settings.legal.entity.form';

    const legalEntityFormRules = computed(() =>
        inferRules(formData, {
            details: {
                billingContact: {
                    firstName: {
                        required,
                    },
                    lastName: {
                        required,
                    },
                    email: {
                        emailValidation: withMessage(emailValidation(false), t(`${baseKey}.contact.email.client.invalid`)),
                    },
                    paymentReminderContact: {
                        emailValidation: emailValidation(true),
                    },
                },
            },
            eInvoiceRouting: {
                identifierType: {},
                routingSuffix: {
                    suffixValidation,
                },
            },
            preferences: {
                invoicingSettings: {
                    invoicingMode: {
                        invoicingTypeValidation: invoicingTypeValidation(
                            () => formData.value.preferences.invoicingSettings?.isSubcontracting,
                            () => formData.value.preferences.paymentSettings?.isInvoiceBeforePayment,
                        ),
                    },
                },
                clientTerms: {
                    required: clientTermsValidation,
                },
                clientTermsAgency: {},
                invoiceMailingSettings: {
                    sendInvoiceByEmailToClient: {},
                },
            },
        }),
    );

    const invoicingTypeValidation = createRule({
        validator: (value: Maybe<InvoicingModeResource>, isSubContracting: Maybe<boolean>, isInvoicingBeforePayment: Maybe<boolean>): boolean => {
            if (enablePrepaymentForUP.value) {
                return true;
            }
            if (isFilled(value) && isFilled(isSubContracting) && isFilled(isInvoicingBeforePayment)) {
                if (value === InvoicingModeResource.Intermediation && isSubContracting && !isInvoicingBeforePayment) {
                    return false;
                }
            }
            return true;
        },
        message: t(`${baseKey}.contracting.model.UP.error`),
    });

    const suffixValidation = createRule({
        validator: (value: Maybe<string>): boolean => {
            if (formData.value.eInvoiceRouting?.identifierType !== EInvoiceIdentifierType.RoutingSuffix) {
                return true;
            }
            return isFilled(value) && /^[A-Za-z0-9_-]+$/.test(value);
        },
        message: t(`${baseKey}.einvoice-routing.suffix.invalid`),
    });

    const emailValidation = createRule({
        type: 'email',
        validator: (value: Maybe<string>, _additional: boolean): boolean => {
            return isFilled(value) && email.exec(value);
        },
        message: ({$params: [additional]}): string => {
            return t(`${baseKey}.contact.${additional ? 'additional-' : ''}email.client.invalid`);
        },
    });

    const clientTermsValidation = createRule({
        type: 'required',
        validator(value: Maybe<File | null>) {
            const ruleApplies = hasEnoughPrivilege(['ROLE_ADMIN']) && (serviceLevelCompanyFeatures?.value?.canUploadClientTerms ?? false);
            if (!ruleApplies) {
                return true;
            }
            const isRequired =
                formData.value.preferences.invoicingSettings?.invoicingMode === InvoicingModeResource.Intermediation &&
                formData.value.preferences.invoicingSettings?.isSubcontracting === true;

            if (!isRequired) {
                return true;
            }
            return isFilled(value);
        },
        message: t('generic.forms.validation.errors.required'),
    });

    return {legalEntityFormRules};
}
